/**
 * Lock-based spinner. Multiple sources can request the spinner concurrently;
 * the spinner stays visible until every lock is released. Each `show()`
 * returns a function that releases its own lock — this prevents the legacy
 * "spinner stuck forever" bug where a missed `hide()` would leave it on screen.
 *
 * A safety watchdog hides the spinner if locks live longer than MAX_DURATION_MS,
 * which is the original behaviour the legacy code attempted via hijacking
 * `window.hideSpinner`.
 */

import { createLogger } from './logger.js';

const log = createLogger('spinner');

const SPINNER_ID = 'spinner';
const LABEL_ID = 'spinnerLabel';
const DEFAULT_LABEL = 'טוען...';
const MAX_DURATION_MS = 15_000;

/** @typedef {{ acquiredAt: number; label: string }} LockInfo */
/** @type {Map<string, LockInfo>} */
const locks = new Map();

let watchdog = /** @type {ReturnType<typeof setTimeout> | null} */ (null);
/** When `>0`, the spinner element stays hidden even with active locks. Used by
 *  modal flows (OTP) that want to keep their long-running parent's spinner
 *  state alive but not visible while a foreground modal is interactive. */
let suspendCount = 0;

function getElement() {
  return document.getElementById(SPINNER_ID);
}

/** Pick the most recently-acquired lock's label so callers can change the
 *  message without releasing/re-acquiring. */
function currentLabel() {
  let latest = /** @type {LockInfo | null} */ (null);
  for (const info of locks.values()) {
    if (!latest || info.acquiredAt > latest.acquiredAt) latest = info;
  }
  return latest?.label ?? DEFAULT_LABEL;
}

function update() {
  const el = getElement();
  if (!el) return;
  const visible = suspendCount === 0 && locks.size > 0;
  el.style.display = visible ? 'flex' : 'none';
  el.setAttribute('aria-hidden', visible ? 'false' : 'true');
  if (visible) {
    const labelEl = document.getElementById(LABEL_ID);
    if (labelEl) labelEl.textContent = currentLabel();
  }
}

function armWatchdog() {
  if (watchdog) clearTimeout(watchdog);
  watchdog = setTimeout(() => {
    if (locks.size === 0) return;
    const now = Date.now();
    /** @type {string[]} */
    const stale = [];
    for (const [tag, info] of locks) {
      if (now - info.acquiredAt > MAX_DURATION_MS) stale.push(tag);
    }
    if (stale.length > 0) {
      log.warn('force-releasing stale spinner locks:', stale);
      stale.forEach((tag) => locks.delete(tag));
      update();
    }
    if (locks.size > 0) armWatchdog();
  }, 5_000);
}

/**
 * Acquire a spinner lock. Returns a release function — call it (or the standalone
 * `hide(tag)`) when the work is done. Idempotent per tag.
 * @param {string} [tag='default']
 * @param {string} [label]  Human-readable label shown under the ring. Defaults
 *                          to the lock tag's previous label or 'טוען...'.
 * @returns {() => void}
 */
export function show(tag = 'default', label) {
  const existing = locks.get(tag);
  if (!existing) {
    locks.set(tag, { acquiredAt: Date.now(), label: label ?? DEFAULT_LABEL });
    update();
    armWatchdog();
  } else if (label && label !== existing.label) {
    // Allow a caller to update the label mid-flight (e.g. "מחפש רכב..." → "מאמת...").
    existing.label = label;
    update();
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    hide(tag);
  };
}

/**
 * Release a spinner lock.
 * @param {string} [tag='default']
 */
export function hide(tag = 'default') {
  if (locks.delete(tag)) update();
}

/** Release all locks. Use only in emergency cleanup (e.g. logout). */
export function forceHide() {
  if (locks.size === 0) return;
  log.warn('forceHide() called with active locks:', [...locks.keys()]);
  locks.clear();
  update();
}

/**
 * Run an async function with the spinner shown for its duration.
 * @template T
 * @param {string} tag
 * @param {() => Promise<T>} fn
 * @param {string} [label]
 * @returns {Promise<T>}
 */
export async function withSpinner(tag, fn, label) {
  const release = show(tag, label);
  try {
    return await fn();
  } finally {
    release();
  }
}

/** Inspect active locks (debugging). */
export function activeLocks() {
  return [...locks.keys()];
}

/**
 * Hide the spinner while keeping locks intact. Returns a `resume` function;
 * call it (or `resume()`) to bring the spinner back if locks still exist.
 *
 * Stackable — nested suspend/resume pairs each get their own counter slot.
 *
 * Use case: a modal (OTP entry) wants to be the sole visual focus while
 * the parent flow's spinner ("מתחבר...") is technically still acquired.
 * When the modal closes, the parent flow continues and the spinner returns
 * automatically.
 *
 * @returns {() => void}
 */
export function suspend() {
  suspendCount++;
  update();
  let resumed = false;
  return () => {
    if (resumed) return;
    resumed = true;
    if (suspendCount > 0) suspendCount--;
    update();
  };
}

/** Force-resume all suspends. Emergency cleanup only. */
export function resume() {
  if (suspendCount === 0) return;
  suspendCount = 0;
  update();
}
